import { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircleMore, X, Send, Paperclip, Check, CheckCheck } from "lucide-react";

// Widget de chat support natif (façon Tidio), réutilisable :
//  - plateforme : client={api}, spaceSlug=""
//  - boutique   : client={shopApi}, spaceSlug=<slug du revendeur>
// Isolation : le backend confine selon l'auth/slug ; rien n'est partagé entre espaces.
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export default function SupportWidget({ client, spaceSlug = "", accent = "#4fa827", title = "Support" }) {
  const storeKey = `support.conv.${spaceSlug || "platform"}`;
  const visKey = "support.visitor.id";

  const [open, setOpen] = useState(false);
  const [conv, setConv] = useState(null);           // { id, guestToken }
  const [meta, setMeta] = useState(null);           // conversation publique (statut, peer_typing, peer_last_read_at)
  const [messages, setMessages] = useState([]);
  const [prechat, setPrechat] = useState({ name: "", email: "", message: "" });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const lastTsRef = useRef("");
  const typingSentRef = useRef(0);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  // Charge la conversation stockée localement.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storeKey);
      if (raw) setConv(JSON.parse(raw));
    } catch { /* noop */ }
  }, [storeKey]);

  // Heartbeat visiteur (IP/pays/ville côté serveur).
  useEffect(() => {
    let vid = "";
    try { vid = localStorage.getItem(visKey) || ""; } catch { /* noop */ }
    if (!vid) { vid = `v_${Date.now()}_${Math.floor(Math.random() * 1e6)}`; try { localStorage.setItem(visKey, vid); } catch { /* noop */ } }
    const beat = () => {
      client.post("/support/visitors/heartbeat", {
        visitor_id: vid, space_slug: spaceSlug, path: window.location.pathname,
        name: prechat.name || "", email: prechat.email || "",
      }).catch(() => {});
    };
    beat();
    const t = setInterval(beat, 30000);
    return () => clearInterval(t);
  }, [client, spaceSlug, prechat.name, prechat.email]);

  const scrollDown = () => setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 60);

  const poll = useCallback(async () => {
    if (!conv?.id) return;
    try {
      const r = await client.get(`/support/conversations/${conv.id}/messages`, {
        params: { guest_token: conv.guestToken, after: lastTsRef.current || undefined },
      });
      setMeta(r.data.conversation);
      const incoming = r.data.messages || [];
      if (incoming.length) {
        lastTsRef.current = incoming[incoming.length - 1].created_at;
        setMessages((prev) => {
          const ids = new Set(prev.map((m) => m.id));
          return [...prev, ...incoming.filter((m) => !ids.has(m.id))];
        });
        scrollDown();
        if (open && incoming.some((m) => m.sender === "staff")) {
          client.post(`/support/conversations/${conv.id}/read`, { guest_token: conv.guestToken }).catch(() => {});
        }
      }
    } catch { /* noop */ }
  }, [client, conv, open]);

  // Polling temps réel quand ouvert.
  useEffect(() => {
    if (!open || !conv?.id) return;
    poll();
    const t = setInterval(poll, 2500);
    return () => clearInterval(t);
  }, [open, conv, poll]);

  const startConversation = async () => {
    if (!prechat.name.trim() || !prechat.email.trim()) return;
    setBusy(true);
    try {
      const r = await client.post("/support/conversations", {
        space_slug: spaceSlug, name: prechat.name.trim(), email: prechat.email.trim(),
        message: prechat.message.trim() || undefined,
      });
      const next = { id: r.data.conversation.id, guestToken: r.data.guest_token };
      try { localStorage.setItem(storeKey, JSON.stringify(next)); } catch { /* noop */ }
      setConv(next); setMeta(r.data.conversation); setMessages([]); lastTsRef.current = "";
    } catch { /* noop */ } finally { setBusy(false); }
  };

  const sendTyping = () => {
    const now = Date.now();
    if (now - typingSentRef.current < 2500) return;
    typingSentRef.current = now;
    client.post(`/support/conversations/${conv.id}/typing`, { guest_token: conv.guestToken }).catch(() => {});
  };

  const sendMessage = async (attachment) => {
    if (!conv?.id) return;
    const body = input.trim();
    if (!body && !attachment) return;
    setInput("");
    try {
      const r = await client.post(`/support/conversations/${conv.id}/messages`, {
        body: body || undefined, attachment, guest_token: conv.guestToken,
      });
      setMessages((prev) => [...prev, r.data.message]);
      lastTsRef.current = r.data.message.created_at;
      scrollDown();
    } catch { /* noop */ }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3_500_000) { alert("Fichier trop volumineux (max ~3 Mo)."); return; }
    const reader = new FileReader();
    reader.onload = () => sendMessage({ name: file.name, type: file.type, data_url: reader.result });
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const peerLastRead = meta?.peer_last_read_at;

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} aria-label="Support"
        style={{ position: "fixed", right: 20, bottom: 20, zIndex: 2147483000, width: 58, height: 58, borderRadius: "50%",
          border: "none", cursor: "pointer", background: accent, color: "#fff", boxShadow: "0 10px 30px rgba(0,0,0,.25)",
          display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="support-widget-button">
        {open ? <X size={24} /> : <MessageCircleMore size={26} />}
      </button>

      {open && (
        <div style={{ position: "fixed", right: 20, bottom: 88, zIndex: 2147483000, width: "min(370px, calc(100vw - 32px))",
          height: "min(560px, calc(100vh - 120px))", background: "#fff", borderRadius: 16, overflow: "hidden",
          boxShadow: "0 24px 70px rgba(0,0,0,.28)", display: "flex", flexDirection: "column", fontFamily: "system-ui, sans-serif" }}
          data-testid="support-widget-panel">
          <div style={{ background: accent, color: "#fff", padding: "16px 18px" }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{title}</div>
            <div style={{ fontSize: 12.5, opacity: .9 }}>Nous répondons au plus vite</div>
          </div>

          {!conv ? (
            <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12, overflowY: "auto" }}>
              <p style={{ margin: 0, fontSize: 13.5, color: "#475063" }}>Avant de démarrer, indiquez votre nom et votre e-mail.</p>
              <input value={prechat.name} onChange={(e) => setPrechat((p) => ({ ...p, name: e.target.value }))} placeholder="Votre nom"
                style={inp} data-testid="support-prechat-name" />
              <input value={prechat.email} onChange={(e) => setPrechat((p) => ({ ...p, email: e.target.value }))} placeholder="Votre e-mail" type="email"
                style={inp} data-testid="support-prechat-email" />
              <textarea value={prechat.message} onChange={(e) => setPrechat((p) => ({ ...p, message: e.target.value }))} placeholder="Votre message (optionnel)"
                rows={3} style={{ ...inp, resize: "none" }} />
              <button onClick={startConversation} disabled={busy || !prechat.name.trim() || !prechat.email.trim()}
                style={{ ...btn(accent), opacity: busy ? .7 : 1 }} data-testid="support-prechat-start">
                Démarrer la conversation
              </button>
            </div>
          ) : (
            <>
              <div style={{ flex: 1, overflowY: "auto", padding: 14, background: "#f5f7fa", display: "flex", flexDirection: "column", gap: 8 }}>
                {messages.length === 0 && <p style={{ textAlign: "center", color: "#9aa3b2", fontSize: 13, marginTop: 20 }}>Envoyez votre premier message.</p>}
                {messages.map((m) => {
                  const mine = m.sender === "user";
                  const read = mine && peerLastRead && peerLastRead >= m.created_at;
                  return (
                    <div key={m.id} style={{ alignSelf: mine ? "flex-end" : "flex-start", maxWidth: "82%" }}>
                      <div style={{ background: mine ? accent : "#fff", color: mine ? "#fff" : "#0f1622", padding: "9px 12px",
                        borderRadius: 12, fontSize: 14, border: mine ? "none" : "1px solid #e6e9ef", wordBreak: "break-word" }}>
                        {m.attachment && (m.attachment.type || "").startsWith("image/")
                          ? <img src={m.attachment.data_url} alt={m.attachment.name} style={{ maxWidth: "100%", borderRadius: 8, marginBottom: m.body ? 6 : 0 }} />
                          : m.attachment && <a href={m.attachment.data_url} download={m.attachment.name} style={{ color: mine ? "#fff" : accent, textDecoration: "underline", fontSize: 13 }}>📎 {m.attachment.name}</a>}
                        {m.body && <div>{m.body}</div>}
                      </div>
                      <div style={{ fontSize: 10.5, color: "#9aa3b2", textAlign: mine ? "right" : "left", marginTop: 2, display: "flex", gap: 4, justifyContent: mine ? "flex-end" : "flex-start", alignItems: "center" }}>
                        {fmtTime(m.created_at)} {mine && (read ? <CheckCheck size={13} style={{ color: accent }} /> : <Check size={13} />)}
                      </div>
                    </div>
                  );
                })}
                {meta?.peer_typing && <div style={{ alignSelf: "flex-start", color: "#9aa3b2", fontSize: 12.5, fontStyle: "italic" }}>En train d'écrire…</div>}
                <div ref={endRef} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 10, borderTop: "1px solid #e6e9ef" }}>
                <input ref={fileRef} type="file" onChange={onFile} style={{ display: "none" }} accept="image/*,.pdf,.doc,.docx,.txt" />
                <span onClick={() => fileRef.current?.click()} style={{ cursor: "pointer", color: "#7a8493" }}><Paperclip size={20} /></span>
                <input value={input} onChange={(e) => { setInput(e.target.value); sendTyping(); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); sendMessage(); } }}
                  placeholder="Écrire un message…" style={{ flex: 1, border: "1px solid #d8dee8", borderRadius: 10, padding: "10px 12px", fontSize: 14, outline: "none", color: "#0f1622" }}
                  data-testid="support-message-input" />
                <span onClick={() => sendMessage()} style={{ cursor: "pointer", color: accent }} data-testid="support-message-send"><Send size={20} /></span>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

const inp = { width: "100%", border: "1px solid #d8dee8", borderRadius: 10, padding: "11px 13px", fontSize: 14, outline: "none", color: "#0f1622" };
const btn = (accent) => ({ width: "100%", padding: 12, borderRadius: 10, border: "none", background: accent, color: "#fff", fontWeight: 700, fontSize: 14.5, cursor: "pointer" });
